/**
 * ContractDialog — Generate New / Refi Contract (08/20).
 *
 * Josh: "make it one for generate new contracts and One for generate refi
 * contracts. Generate refi contracts will allow me to send out contracts And
 * deduct the balance that it is currently. Therefore it will net the merchant
 * the remainder."
 *
 * Operator enters the NEW terms (funding, factor, term, schedule, fees); the
 * refi variant auto-pulls the deal's current effective balance as the payoff
 * deduction. Funded-deal pages can download internal records, but prospective
 * signing requests now start from the merchant contact profile.
 */
import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { toast } from 'sonner';
import { FileText, FileSignature } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fmt = (v) => (isFinite(v) ? v.toLocaleString('en-US', { style: 'currency', currency: 'USD' }) : '—');

const ContractDialog = ({ open, onOpenChange, deal, kind, getAuthHeaders, onSendViaSignport }) => {
  const isRefi = kind === 'refi';
  const [funding, setFunding] = useState('');
  const [factor, setFactor] = useState('');
  const [termDays, setTermDays] = useState('');
  const [schedule, setSchedule] = useState('Daily');
  const [paymentAmount, setPaymentAmount] = useState('');
  const [origFee, setOrigFee] = useState('');
  const [otherFees, setOtherFees] = useState('');
  const [payoff, setPayoff] = useState('');
  const [downloading, setDownloading] = useState(false);

  useEffect(() => {
    if (!open) return;
    // Fresh form each open; refi prefills the current effective balance.
    setFunding(''); setFactor(''); setTermDays(''); setSchedule('Daily');
    setPaymentAmount(''); setOrigFee(''); setOtherFees('');
    setPayoff(isRefi ? String(deal?.balance_with_fees ?? deal?.balance ?? '') : '');
  }, [open, isRefi, deal?.balance_with_fees, deal?.balance]);

  const f = parseFloat(funding) || 0;
  const r = parseFloat(factor) || 0;
  const payback = useMemo(() => Math.round(f * r * 100) / 100, [f, r]);
  const fees = (parseFloat(origFee) || 0) + (parseFloat(otherFees) || 0);
  const payoffN = isRefi ? (parseFloat(payoff) || 0) : 0;
  const net = Math.round((f - fees - payoffN) * 100) / 100;
  const suggestedPayment = useMemo(() => {
    const t = parseInt(termDays, 10);
    if (!payback || !t) return null;
    return Math.round((payback / t) * 100) / 100;
  }, [payback, termDays]);

  const validate = () => {
    if (!f || !r) { toast.error('Funding amount and factor are required'); return false; }
    if (isRefi && net < 0) { toast.error('Net to merchant is negative — check the payoff/fees'); return false; }
    return true;
  };

  const buildTerms = () => ({
    funding_amount: f,
    factor: r,
    term_days: parseInt(termDays, 10) || null,
    schedule,
    payment_amount: parseFloat(paymentAmount) || null,
    origination_fee: parseFloat(origFee) || 0,
    other_fees: parseFloat(otherFees) || 0,
    payoff_balance: isRefi ? payoffN : null,
  });

  const downloadPdf = async () => {
    if (!validate()) return;
    setDownloading(true);
    try {
      const res = await axios.post(
        `${API}/deals/${deal.id}/documents/contract`,
        { kind, terms: buildTerms() },
        { headers: getAuthHeaders(), responseType: 'blob' }
      );
      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      const link = document.createElement('a');
      link.href = url;
      const label = isRefi ? 'Refi_Contract' : 'New_Contract';
      link.setAttribute('download', `${label}_${deal?.deal_id || 'deal'}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Contract downloaded');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to generate contract');
    } finally {
      setDownloading(false);
    }
  };

  const sendViaSignport = () => {
    if (!onSendViaSignport) return;
    if (!validate()) return;
    const t = buildTerms();
    onSendViaSignport({
      funding_amount: t.funding_amount,
      payback,
      factor: t.factor,
      term_days: t.term_days,
      schedule: t.schedule,
      payment_amount: t.payment_amount,
      fees,
      payoff_balance: isRefi ? payoffN : null,
      net_amount: net,
    });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>
            {isRefi ? 'Generate Refi Contract' : 'Generate New Contract'} — Deal #{deal?.deal_id}
          </DialogTitle>
          <DialogDescription>
            {isRefi
              ? 'Enter the new terms. The current balance is deducted from the disbursement — the merchant nets the remainder.'
              : 'Enter the terms for the new advance.'}
          </DialogDescription>
        </DialogHeader>

        <div className="grid grid-cols-2 gap-3">
          <div className="space-y-1">
            <Label>Funding Amount *</Label>
            <Input type="number" value={funding} onChange={e => setFunding(e.target.value)}
              placeholder="10000" data-testid="contract-funding" />
          </div>
          <div className="space-y-1">
            <Label>Factor Rate *</Label>
            <Input type="number" step="0.01" value={factor} onChange={e => setFactor(e.target.value)}
              placeholder="1.45" data-testid="contract-factor" />
          </div>
          <div className="space-y-1">
            <Label>Term (days)</Label>
            <Input type="number" value={termDays} onChange={e => setTermDays(e.target.value)}
              placeholder="60" data-testid="contract-term" />
          </div>
          <div className="space-y-1">
            <Label>Schedule</Label>
            <Select value={schedule} onValueChange={setSchedule}>
              <SelectTrigger data-testid="contract-schedule"><SelectValue /></SelectTrigger>
              <SelectContent>
                {['Daily', 'Weekly', 'Biweekly', 'Monthly'].map(s => (
                  <SelectItem key={s} value={s}>{s}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1">
            <Label>Payment Amount</Label>
            <Input type="number" value={paymentAmount} onChange={e => setPaymentAmount(e.target.value)}
              placeholder={suggestedPayment ? String(suggestedPayment) : ''} data-testid="contract-payment" />
            {suggestedPayment && !paymentAmount && (
              <button type="button" className="text-xs text-blue-600 hover:underline"
                onClick={() => setPaymentAmount(String(suggestedPayment))}>
                Use {fmt(suggestedPayment)} ({schedule.toLowerCase()}, {termDays}d)
              </button>
            )}
          </div>
          <div className="space-y-1">
            <Label>Origination Fee</Label>
            <Input type="number" value={origFee} onChange={e => setOrigFee(e.target.value)}
              placeholder="0" data-testid="contract-orig-fee" />
          </div>
          <div className="space-y-1">
            <Label>Other Fees</Label>
            <Input type="number" value={otherFees} onChange={e => setOtherFees(e.target.value)}
              placeholder="0" data-testid="contract-other-fees" />
          </div>
          {isRefi && (
            <div className="space-y-1">
              <Label>Payoff — Current Balance</Label>
              <Input type="number" value={payoff} onChange={e => setPayoff(e.target.value)}
                data-testid="contract-payoff" />
              <div className="text-[11px] text-gray-500">
                Auto-pulled: effective balance incl. fees
              </div>
            </div>
          )}
        </div>

        {/* Live math preview */}
        <div className="rounded-md border bg-gray-50 p-3 text-sm space-y-1" data-testid="contract-preview">
          <div className="flex justify-between"><span>Payback ({funding || 0} × {factor || 0}):</span>
            <span className="font-medium">{fmt(payback)}</span></div>
          {fees > 0 && (
            <div className="flex justify-between text-gray-600"><span>Less fees:</span>
              <span>({fmt(fees)})</span></div>
          )}
          {isRefi && (
            <div className="flex justify-between text-gray-600"><span>Less payoff (this deal):</span>
              <span>({fmt(payoffN)})</span></div>
          )}
          <div className="flex justify-between font-semibold border-t pt-1">
            <span>Net to merchant:</span>
            <span className={net < 0 ? 'text-red-600' : 'text-emerald-700'}>{fmt(net)}</span>
          </div>
        </div>

        <div className="flex justify-end gap-2 pt-1">
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button variant="outline" onClick={downloadPdf} disabled={downloading}
            data-testid="contract-download-btn">
            <FileText className="h-4 w-4 mr-1.5" />
            {downloading ? 'Generating…' : 'Download PDF'}
          </Button>
          {onSendViaSignport && (
            <Button onClick={sendViaSignport} data-testid="contract-signport-btn">
              <FileSignature className="h-4 w-4 mr-1.5" />
              Send via Signport
            </Button>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default ContractDialog;
